import React, { useState } from 'react';
import { CONFIG } from '@/services';

interface TestResult {
  endpoint: string;
  status: 'pending' | 'success' | 'error';
  message: string;
  data?: any;
  timestamp?: string;
}

const CorsTest: React.FC = () => {
  const [results, setResults] = useState<TestResult[]>([]);
  const [isRunning, setIsRunning] = useState(false);

  const updateResult = (endpoint: string, status: TestResult['status'], message: string, data?: any) => {
    setResults(prev => {
      const existingIndex = prev.findIndex(r => r.endpoint === endpoint);
      const newResult: TestResult = {
        endpoint,
        status,
        message,
        data,
        timestamp: new Date().toLocaleTimeString()
      };
      
      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex] = newResult;
        return updated;
      } else {
        return [...prev, newResult];
      }
    });
  };

  const testEndpoint = async (endpoint: string, description: string) => {
    updateResult(endpoint, 'pending', `Testing ${description}...`);
    
    try {
      const response = await fetch(`${CONFIG.backend.baseUrl}${endpoint}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      updateResult(endpoint, 'success', `✅ ${description} successful`, data);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      updateResult(endpoint, 'error', `❌ ${description} failed: ${errorMessage}`);
    }
  };

  const runAllTests = async () => {
    setIsRunning(true);
    setResults([]);

    const tests = [
      { endpoint: '/api/health', description: 'Health Check' },
      { endpoint: '/api/health/cors', description: 'CORS Test' },
    ];

    // Run tests sequentially
    for (const test of tests) {
      await testEndpoint(test.endpoint, test.description);
      await new Promise(resolve => setTimeout(resolve, 500)); // Small delay between tests
    }

    setIsRunning(false);
  };

  const clearResults = () => {
    setResults([]);
  };

  const getStatusColor = (status: TestResult['status']) => {
    switch (status) {
      case 'success': return 'text-green-600';
      case 'error': return 'text-red-600';
      case 'pending': return 'text-yellow-600';
      default: return 'text-gray-600';
    }
  };

  const getStatusIcon = (status: TestResult['status']) => {
    switch (status) {
      case 'success': return '✅';
      case 'error': return '❌';
      case 'pending': return '⏳';
      default: return '⚪';
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-800">CORS & API Connection Test</h2>
        <div className="space-x-2">
          <button
            onClick={runAllTests}
            disabled={isRunning}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isRunning ? 'Running Tests...' : 'Run Tests'}
          </button>
          <button
            onClick={clearResults}
            disabled={isRunning}
            className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Clear Results
          </button>
        </div>
      </div>

      <div className="mb-4 p-4 bg-gray-50 rounded-md">
        <h3 className="font-semibold text-gray-700 mb-2">Configuration Info:</h3>
        <div className="text-sm text-gray-600 space-y-1">
          <div><strong>Backend URL:</strong> {CONFIG.backend.baseUrl}</div>
          <div><strong>Frontend URL:</strong> {window.location.origin}</div>
          <div><strong>Environment:</strong> {import.meta.env.MODE}</div>
        </div>
      </div>

      {results.length === 0 && !isRunning && (
        <div className="text-center py-8 text-gray-500">
          Click "Run Tests" to test CORS and API connectivity
        </div>
      )}

      {results.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-lg font-semibold text-gray-800">Test Results:</h3>
          {results.map((result, index) => (
            <div
              key={index}
              className={`p-4 border rounded-md ${
                result.status === 'success' ? 'border-green-200 bg-green-50' :
                result.status === 'error' ? 'border-red-200 bg-red-50' :
                'border-yellow-200 bg-yellow-50'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <span className="text-lg">{getStatusIcon(result.status)}</span>
                  <span className="font-medium">{result.endpoint}</span>
                  {result.timestamp && (
                    <span className="text-sm text-gray-500">({result.timestamp})</span>
                  )}
                </div>
                <span className={`text-sm font-medium ${getStatusColor(result.status)}`}>
                  {result.status.toUpperCase()}
                </span>
              </div>
              
              <div className={`text-sm ${getStatusColor(result.status)}`}>
                {result.message}
              </div>
              
              {result.data && (
                <details className="mt-2">
                  <summary className="cursor-pointer text-sm text-gray-600 hover:text-gray-800">
                    Show Response Data
                  </summary>
                  <pre className="mt-2 p-2 bg-gray-100 rounded text-xs overflow-x-auto">
                    {JSON.stringify(result.data, null, 2)}
                  </pre>
                </details>
              )}
            </div>
          ))}
        </div>
      )}

      {isRunning && (
        <div className="text-center py-4">
          <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">Running tests...</p>
        </div>
      )}
    </div>
  );
};

export default CorsTest;